/** Suppliers - *who has answered, and what it is worth* (ADR-0195, the fourth fixed view).
 *
 * The buyer's side of the exchange only: requests by where they have got to, the ones past their
 * date, the answers waiting for the buyer to take responsibility for them, how each supplier has
 * answered over time, and the agreements that are revoked. A request this workspace *answers* (it
 * is the supplier) is not drawn here and the view says so; the supplier's own page is
 * `/suppliers`.
 *
 * **"Answered" is not "accepted".** A supplier answering is not the buyer agreeing to put the
 * figure in the inventory (`RequestState`), so the two are separate bars and the waiting ones are
 * listed. **A median is shown only when the server says it is ready** (three answers or more);
 * otherwise the tile says why there is none, never a number from two points.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useAgreements, useExchange } from "../../api/suppliers";
import { useSupplierResponsiveness } from "../../api/supplierResponsiveness";
import { StatusChip } from "../../components/ui/StatusChip";
import { shortDate } from "../../lib/format";
import { isoToday } from "../readinessModel";
import { stateLabel, supplierName, supplierNumbers } from "../supplierLabels";
import type { Screen } from "../useScreen";
import { carryOf, overdueRequests, requestBars, waitingToBeAccepted } from "./dashboardModel";
import styles from "./Dashboards.module.css";
import { BarRows } from "./parts";
import type { BarRow } from "./parts";
import { Tile } from "./Tile";

export function SuppliersView({ screen }: { screen: Screen }) {
  const carry = carryOf(screen);
  const [today] = useState(() => isoToday(new Date()));
  const exchange = useExchange();
  const responsiveness = useSupplierResponsiveness();
  const agreements = useAgreements();

  const numbers = supplierNumbers(exchange.data ?? []);
  const buyerSide = (exchange.data ?? []).filter((request) => request.i_am === "buyer");
  const supplierSide = (exchange.data ?? []).length - buyerSide.length;
  const overdue = overdueRequests(buyerSide, today);
  const waiting = waitingToBeAccepted(buyerSide);
  const revoked = (agreements.data ?? []).filter((agreement) => agreement.i_am === "buyer" && agreement.revoked_at);

  return (
    <div className={styles.view} data-testid="dashboard-suppliers">
      <div className={styles.span6}>
        <Tile
          id="suppliers-requests"
          title="Requests by where they stand"
          sub="What this workspace has asked its suppliers for"
          of="The supplier requests"
          reads={[exchange]}
          action={
            <Link to="/suppliers" search={carry} className={styles.link}>
              Suppliers
            </Link>
          }
        >
          {buyerSide.length === 0 ? (
            <p className={styles.empty} data-testid="dash-no-requests">
              {supplierSide > 0
                ? "This workspace answers requests from its customers and has asked none of its own. Its answers are on Suppliers."
                : "No request has been raised. Asking a supplier for a figure starts on Suppliers."}
            </p>
          ) : (
            <BarRows
              testId="dash-requests"
              caption="Supplier requests by state"
              head={["State", "Requests"]}
              rows={requestBars(buyerSide).map(
                (bar): BarRow => ({
                  id: bar.state,
                  name: stateLabel(bar.state),
                  fraction: bar.fraction,
                  figure: String(bar.count),
                  fill: bar.state === "accepted" ? "info" : bar.state === "answered" ? "warn" : "muted",
                  state: bar.count === 0 ? "outline" : "drawn",
                  to: { to: "/suppliers", search: carry },
                  openLabel: `${stateLabel(bar.state)}: ${bar.count} requests. Open Suppliers.`,
                }),
              )}
            />
          )}
        </Tile>
      </div>

      <div className={styles.span6}>
        <Tile
          id="suppliers-waiting"
          title="Needs a decision from you"
          sub="Answers to accept and requests past their date"
          of="The supplier requests"
          reads={[exchange]}
        >
          {waiting.length + overdue.length === 0 ? (
            <p className={styles.empty}>No answer is waiting to be accepted and no request is past its date.</p>
          ) : (
            <ul className={styles.rows} data-testid="dash-supplier-attention">
              {[...waiting.map((r) => ({ r, kind: "answer" as const })), ...overdue.map((r) => ({ r, kind: "overdue" as const }))]
                .slice(0, 8)
                .map(({ r, kind }) => (
                  <li key={`${kind}-${r.id}`} data-kind={kind}>
                    <Link
                      to="/suppliers"
                      search={carry}
                      className={`${styles.row} ${styles.rowLink}`}
                      aria-label={`${kind === "answer" ? "An answer to accept" : "A request past its date"}: ${r.fuel_or_carrier.replace(/_/g, " ")}. Open Suppliers.`}
                    >
                      <span className={styles.name}>
                        {r.fuel_or_carrier.replace(/_/g, " ")}
                        <span className={styles.sub}>
                          {shortDate(r.period_start)} to {shortDate(r.period_end)}
                        </span>
                      </span>
                      <span>
                        <StatusChip tone="warn" size="sm">
                          {kind === "answer" ? "Answer to accept" : `Due ${shortDate(r.due_on)}`}
                        </StatusChip>
                      </span>
                      <span className={styles.words}>Open</span>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </Tile>
      </div>

      <div className={styles.span8}>
        <Tile
          id="suppliers-responsiveness"
          title="How each supplier has answered"
          sub="Over every request this workspace has raised"
          of="Supplier responsiveness"
          reads={[responsiveness]}
        >
          {(responsiveness.data ?? []).length === 0 ? (
            <p className={styles.empty}>No supplier has been asked yet, so there is no answering history.</p>
          ) : (
            <ul className={styles.rows} data-testid="dash-responsiveness">
              {[...(responsiveness.data ?? [])]
                .sort((a, b) => b.outstanding - a.outstanding || b.created - a.created)
                // **The number stays, and that is a boundary rather than an omission.** A supplier is
                // its own tenant (ADR-0040). `organization` is readable only for the session's own
                // workspace and the ones the signed-in person belongs to (`organization_policy_
                // statements`), and `SHARED_TABLES` is exactly three tables, none carrying the
                // counterparty's legal name. So the buyer's read has no name to show, and giving
                // it one means widening what crosses a tenant boundary (a policy on `organization`
                // or a name column on a request), which ADR-0040 says needs its own argument and is
                // not made here (ADR-0221, proposed). A supplier is `Supplier N` in the order it was
                // first asked, the same on every screen (`supplierNumbers`); the rows are ranked by
                // outstanding count. The link opens the supplier's own page.
                .map((record) => (
                  <li key={record.supplier_org_id}>
                    <Link
                      to="/suppliers/$supplierId"
                      params={{ supplierId: record.supplier_org_id }}
                      search={carry}
                      className={`${styles.row} ${styles.rowLink}`}
                      aria-label={`${supplierName(record.supplier_org_id, numbers)}: ${record.answered} of ${record.created} answered, ${record.outstanding} outstanding. Open this supplier.`}
                    >
                      <span className={styles.name}>
                        {supplierName(record.supplier_org_id, numbers)}
                        <span className={styles.sub}>
                          {record.answered} of {record.created} answered
                          {record.declined ? `, ${record.declined} declined` : ""}
                        </span>
                      </span>
                      <span className={styles.track} aria-hidden="true">
                        <span
                          className={`${styles.fill} ${styles.fillInfo}`}
                          style={{ width: `${record.created > 0 ? (record.answered / record.created) * 100 : 0}%` }}
                        />
                      </span>
                      <span className={styles.figure}>
                        {record.outstanding} waiting
                        <span className={styles.sub}>
                          {record.timing_status === "ready" && record.median_days !== null
                            ? `median ${record.median_days} days`
                            : "no median yet"}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
          <p className={styles.note}>
            A median is shown only once three answers exist. The share of the Scope 3 total resting on a
            supplier&rsquo;s figures is on that supplier&rsquo;s page.
          </p>
        </Tile>
      </div>

      <div className={styles.span4}>
        <Tile
          id="suppliers-agreements"
          title="Agreements revoked"
          sub="A revoked agreement stops the next copy"
          of="The agreements"
          reads={[agreements]}
        >
          <p className={styles.big} data-testid="dash-revoked">
            {revoked.length}
            <span className={styles.bigUnit}>revoked</span>
          </p>
          <p className={styles.note}>
            Revoking stops a figure being copied into the inventory from then on and un-makes nothing
            already copied.
          </p>
        </Tile>
      </div>
    </div>
  );
}
